import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { resolveInternalAppRoute, resolveNativeAppRoute } from '@/lib/native-app-routes';

export function NativeAppUrlListener() {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRouteRef = useRef(`${location.pathname}${location.search}${location.hash}`);

  useEffect(() => {
    currentRouteRef.current = `${location.pathname}${location.search}${location.hash}`;
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    let disposed = false;

    const navigateFromUrl = (rawUrl: string) => {
      if (disposed) return;
      const route = resolveNativeAppRoute(rawUrl);
      if (!route) {
        return;
      }

      navigate(route, { replace: currentRouteRef.current === route });
    };

    const navigateToRoute = (rawRoute: string) => {
      if (disposed) return;
      const route = resolveInternalAppRoute(rawRoute);
      if (route) {
        navigate(route, { replace: currentRouteRef.current === route });
      }
    };

    void CapacitorApp.getLaunchUrl()
      .then((launchUrl) => {
        if (!disposed && launchUrl?.url) {
          navigateFromUrl(launchUrl.url);
        }
      })
      .catch(() => undefined);

    let listenerHandle: { remove: () => Promise<void> } | null = null;
    let backButtonHandle: { remove: () => Promise<void> } | null = null;
    const removeHandle = (handle: { remove: () => Promise<void> }) => {
      void handle.remove().catch(() => undefined);
    };

    void CapacitorApp.addListener('appUrlOpen', (event) => {
      navigateFromUrl(event.url);
    })
      .then((handle) => {
        if (disposed) {
          removeHandle(handle);
        } else {
          listenerHandle = handle;
        }
      })
      .catch(() => undefined);

    void CapacitorApp.addListener('backButton', ({ canGoBack }) => {
      if (disposed) return;
      const backEvent = new CustomEvent('vital-mobile-back-button', { cancelable: true });
      window.dispatchEvent(backEvent);
      if (backEvent.defaultPrevented) return;

      if (canGoBack || window.history.length > 1) {
        navigate(-1);
        return;
      }

      void CapacitorApp.exitApp();
    })
      .then((handle) => {
        if (disposed) {
          removeHandle(handle);
        } else {
          backButtonHandle = handle;
        }
      })
      .catch(() => undefined);

    const handlePushRoute = (event: Event) => {
      const route = (event as CustomEvent<{ route?: string }>).detail?.route;
      if (route) {
        navigateToRoute(route);
      }
    };

    window.addEventListener('vital-push-notification-route', handlePushRoute);

    return () => {
      disposed = true;
      if (listenerHandle) {
        removeHandle(listenerHandle);
      }
      if (backButtonHandle) {
        removeHandle(backButtonHandle);
      }
      window.removeEventListener('vital-push-notification-route', handlePushRoute);
    };
  }, [navigate]);

  return null;
}
